import { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Input } from '@components/Input';
import { MultiSelect } from '@components/MultiSelect';
import PhoneInput from '@components/PhoneInput';
import { Typography } from '@components/Typography';
import { useTranslation, useYupValidationResolver } from '@hooks';
import usePermissions from '@hooks/usePermissions';
import { accountActions, alertActions } from '@actions';
import { authService, teamsService } from '@services';
import { handleApiError } from '@helpers/api';
import { setInitialPhoneNumberToForm, preparePhoneNumberForSubmit } from '@helpers/phone';
import TeamEditModal, { FORM_TYPE as TEAM_FORM_TYPE } from '../../TeamManagement/TeamEditModal';
import { Modal, Content } from './styles';

export const FORM_TYPE = {
    NEW: 'NEW',
    EDIT: 'EDIT',
};

const EditModal = ({ selectedUser, setSelectedUser, modal, setModal, account }) => {
    const [availableTeams, setAvailableTeams] = useState([]);
    const [teamModal, setTeamModal] = useState({ open: false, type: TEAM_FORM_TYPE.NEW });

    const { t } = useTranslation();
    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const dispatch = useDispatch();
    const { hasFullSettings } = usePermissions();
    const brands = useSelector(state => state.brands?.data || []);

    const UserSchema = yup.object().shape({
        firstName: yup.string().required('validation.required'),
        email: yup.string().required('validation.required').email('validation.wrongEmailFormat'),
        phoneNumber: yup.string().validatePhone('validation.wrongPhoneFormat', true),
        teamIds: yup.array().min(1, 'validation.required'),
    });

    const resolver = useYupValidationResolver(UserSchema, () => ({ dirtyFields, touchedFields }));

    const {
        getValues,
        setValue,
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting, dirtyFields, touchedFields },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            teamIds: [],
        },
    });

    const teamsOptions = useMemo(
        () => availableTeams.map(team => ({ value: team.id, label: team.name })),
        [availableTeams],
    );

    const fetchTeams = () => {
        teamsService
            .getTeams()
            .then(teams => setAvailableTeams(teams || []))
            .catch(() => setAvailableTeams([]));
    };

    useEffect(() => {
        fetchTeams();
    }, []);

    useEffect(() => {
        setValue('phoneNumber', setInitialPhoneNumberToForm(selectedUser?.phoneNumber, account?.locale), {
            shouldValidate: !!selectedUser?.phoneNumber,
            shouldTouch: true,
        });
        if (modal.type !== FORM_TYPE.NEW) {
            setValue('firstName', selectedUser?.firstName || '', { shouldValidate: true });
            setValue('lastName', selectedUser?.lastName || '');
            setValue('email', selectedUser?.email || '', { shouldValidate: true });
            setValue('teamIds', selectedUser?.teams?.map(team => team.id) || [], { shouldValidate: true });
        }
    }, [selectedUser, modal.type, setValue]);

    const onClose = () => {
        setModal({ open: false, type: FORM_TYPE.NEW });
        setSelectedUser(null);
    };

    const RESPONSE_MSG = {
        [FORM_TYPE.EDIT]: { success: t('alertMessages.editSuccess'), error: t('alertMessages.editFail') },
        [FORM_TYPE.NEW]: { success: t('alertMessages.createSuccess'), error: t('alertMessages.createFail') },
    };

    const onSubmit = async () => {
        const values = getValues();
        try {
            await authService.updateUser({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phoneNumber: preparePhoneNumberForSubmit(values.phoneNumber, true),
                teamIds: values.teamIds,
                ...(modal.type === FORM_TYPE.EDIT && { id: selectedUser.id }),
            });
            await dispatch(accountActions.get());
            dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
            onClose();
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error, { email: values.email });
        }
    };

    return (
        <>
            <Modal
                fullScreen
                isOpen={modal.open}
                handleClose={onClose}
                isDirty={isDirty}
                title={modal.type === FORM_TYPE.NEW ? t('buttons.addUser') : t('SettingsUsers.editUser')}
                onPrimaryAction={handleSubmit(onSubmit)}
                primaryActionText={t('buttons.save')}
                primaryActionDisabled={isSubmitting || !isValid || !isDirty}
            >
                <Content>
                    <Typography
                        variant="subtitle1"
                        sx={{ color: 'var(--text-primary)', gridColumn: !isMobile && '1 / span 2' }}
                    >
                        {t('SettingsUsers.generalInfo')}
                    </Typography>
                    <Controller
                        control={control}
                        name="firstName"
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                inputProps={{ ...field }}
                                label={t('common.firstName')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="lastName"
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('common.lastNameOpt')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="email"
                        render={({ field, fieldState: { error } }) => (
                            <Input
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('common.email')}
                                fullWidth
                                disabled={!!selectedUser}
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="phoneNumber"
                        render={({ field, fieldState: { error } }) => (
                            <PhoneInput
                                placeholder=""
                                label={t('common.phoneNumber')}
                                inputProps={{ name: 'phoneNumber' }}
                                value={field.value}
                                autoFormat
                                onChange={value => {
                                    setValue('phoneNumber', value, {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                        shouldTouch: true,
                                    });
                                }}
                                error={error ? t(error.message) : null}
                            />
                        )}
                    />
                    <Typography
                        variant="subtitle1"
                        sx={{ color: 'var(--text-primary)', gridColumn: !isMobile && '1 / span 2' }}
                    >
                        {t('common.teams')}
                    </Typography>
                    <Controller
                        control={control}
                        name="teamIds"
                        render={({ field, fieldState: { error }, ref }) => (
                            <MultiSelect
                                {...field}
                                ref={ref}
                                label={t('common.teams')}
                                onChange={value => {
                                    setValue('teamIds', value, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }}
                                options={teamsOptions}
                                countable
                                required
                                creatable={hasFullSettings()}
                                creatableText={t('SettingsTeams.createTeam')}
                                creatableClick={() => setTeamModal({ open: true, type: TEAM_FORM_TYPE.NEW })}
                                error={error}
                                style={{ gridColumn: !isMobile && '1 / span 2' }}
                            />
                        )}
                    />
                </Content>
            </Modal>
            {teamModal.open && (
                <TeamEditModal
                    selectedTeam={null}
                    setSelectedTeam={() => {}}
                    modal={teamModal}
                    setModal={setTeamModal}
                    brands={brands}
                    account={account}
                    onSaved={async createdTeam => {
                        setAvailableTeams(teams => [...teams, createdTeam]);
                        setValue('teamIds', [...getValues('teamIds'), createdTeam.id], {
                            shouldDirty: true,
                            shouldValidate: true,
                        });
                    }}
                />
            )}
        </>
    );
};

export default EditModal;
